import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import MainNavigation from '../../components/ui/MainNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const CRMDashboardOverview = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalQuotes: 0,
    openQuotes: 0,
    wonQuotes: 0,
    lostQuotes: 0,
    totalSalesValue: 0,
    conversionRate: 0,
    averageQuoteValue: 0,
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [quoteStatusData, setQuoteStatusData] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);

  useEffect(() => {
    if (!authLoading && (!user || !['provider', 'supplier', 'sales_agent', 'reseller', 'superadmin']?.includes(userProfile?.user_type))) {
      navigate('/provider-login');
    }
  }, [user, userProfile, authLoading, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get all quotes
      const { data: quotes, error: quotesError } = await supabase?.from('provider_quotes')?.select('*')?.eq('provider_id', user?.id)?.order('created_at', { ascending: false });

      if (quotesError) throw quotesError;

      // Get all sales
      const { data: sales, error: salesError } = await supabase?.from('provider_sales')?.select('*')?.eq('provider_id', user?.id);

      if (salesError) throw salesError;

      // Calculate metrics
      const totalQuotes = quotes?.length || 0;
      const openQuotes = quotes?.filter(q => ['concept', 'verstuurd', 'in_overweging']?.includes(q?.status))?.length || 0;
      const wonQuotes = quotes?.filter(q => q?.status === 'gewonnen')?.length || 0;
      const lostQuotes = quotes?.filter(q => q?.status === 'verloren')?.length || 0;
      const totalSalesValue = sales?.reduce((sum, sale) => sum + parseFloat(sale?.sale_amount || 0), 0) || 0;
      const conversionRate = totalQuotes > 0 ? Math.round((wonQuotes / totalQuotes) * 100) : 0;
      const averageQuoteValue = totalQuotes > 0 ? Math.round(quotes?.reduce((sum, q) => sum + parseFloat(q?.quote_amount || 0), 0) / totalQuotes) : 0;

      setMetrics({
        totalQuotes,
        openQuotes,
        wonQuotes,
        lostQuotes,
        totalSalesValue,
        conversionRate,
        averageQuoteValue,
      });

      // Prepare monthly sales data (last 6 months)
      const monthlyMap = {};
      const currentDate = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthKey = date?.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' });
        monthlyMap[monthKey] = { month: monthKey, verkopen: 0, offertes: 0 };
      }

      sales?.forEach(sale => {
        const saleDate = new Date(sale?.created_at);
        const monthKey = saleDate?.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' });
        if (monthlyMap?.[monthKey]) {
          monthlyMap[monthKey].verkopen += parseFloat(sale?.sale_amount || 0);
        }
      });

      quotes?.forEach(quote => {
        const quoteDate = new Date(quote?.created_at);
        const monthKey = quoteDate?.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' });
        if (monthlyMap?.[monthKey]) {
          monthlyMap[monthKey].offertes += 1;
        }
      });

      setMonthlyData(Object.values(monthlyMap));

      // Prepare quote status distribution
      const statusMap = {
        concept: { name: 'Concept', value: 0, color: '#94a3b8' },
        verstuurd: { name: 'Verstuurd', value: 0, color: '#3b82f6' },
        in_overweging: { name: 'In Overweging', value: 0, color: '#f59e0b' },
        gewonnen: { name: 'Gewonnen', value: 0, color: '#10b981' },
        verloren: { name: 'Verloren', value: 0, color: '#ef4444' },
      };

      quotes?.forEach(quote => {
        if (statusMap?.[quote?.status]) {
          statusMap[quote?.status].value += 1;
        }
      });

      setQuoteStatusData(Object.values(statusMap)?.filter(s => s?.value > 0));

      // Get recent quotes
      setRecentQuotes(quotes?.slice(0, 5) || []);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const metricCards = [
    {
      label: 'Totaal Offertes',
      value: metrics?.totalQuotes,
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      subtext: `${metrics?.openQuotes} open`,
    },
    {
      label: 'Conversiepercentage',
      value: `${metrics?.conversionRate}%`,
      icon: 'TrendingUp',
      color: 'text-success',
      bgColor: 'bg-success/10',
      subtext: `${metrics?.wonQuotes} gewonnen`,
    },
    {
      label: 'Totale Verkoopwaarde',
      value: `€${metrics?.totalSalesValue?.toLocaleString('nl-NL')}`,
      icon: 'Euro',
      color: 'text-info',
      bgColor: 'bg-info/10',
      subtext: 'Dit jaar',
    },
    {
      label: 'Gem. Offertebedrag',
      value: `€${metrics?.averageQuoteValue?.toLocaleString('nl-NL')}`,
      icon: 'Calculator',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      subtext: 'Per offerte',
    },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      concept: 'bg-muted text-muted-foreground border-border',
      verstuurd: 'bg-info/10 text-info border-info/20',
      in_overweging: 'bg-warning/10 text-warning border-warning/20',
      gewonnen: 'bg-success/10 text-success border-success/20',
      verloren: 'bg-error/10 text-error border-error/20',
    };
    return styles?.[status] || styles?.concept;
  };

  const getStatusLabel = (status) => {
    const labels = {
      concept: 'Concept',
      verstuurd: 'Verstuurd',
      in_overweging: 'In Overweging',
      gewonnen: 'Gewonnen',
      verloren: 'Verloren',
    };
    return labels?.[status] || status;
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Dashboard laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
        {/* Header */}
        <div className="bg-card border-b border-border shadow-elevation-1 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">CRM Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-1">Overzicht van offertes en verkopen</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" iconName="Download" onClick={() => alert('Export functionaliteit komt binnenkort')}>Exporteren</Button>
              <Button iconName="Plus" onClick={() => navigate('/quote-management-system')}>Nieuwe Offerte</Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {metricCards?.map((metric, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className={`${metric?.bgColor} p-3 rounded-lg`}>
                    <Icon name={metric?.icon} size={24} className={metric?.color} />
                  </div>
                </div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-1">{metric?.value}</h3>
                <p className="text-sm text-muted-foreground">{metric?.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{metric?.subtext}</p>
              </div>
            ))}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Sales Chart */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-heading font-semibold text-foreground mb-4">Verkopen per Maand</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                  <Legend />
                  <Line type="monotone" dataKey="verkopen" stroke="#10b981" strokeWidth={2} name="Verkopen (€)" />
                  <Line type="monotone" dataKey="offertes" stroke="#3b82f6" strokeWidth={2} name="Offertes (#)" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Quote Status Distribution */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-heading font-semibold text-foreground mb-4">Offerte Status Verdeling</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={quoteStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {quoteStatusData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Quotes */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-heading font-semibold text-foreground">Recente Offertes</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/quote-management-system')}>Alles Bekijken</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Offertenummer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Klant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Bedrag</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Datum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentQuotes?.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                        Geen recente offertes
                      </td>
                    </tr>
                  ) : (
                    recentQuotes?.map((quote) => (
                      <tr key={quote?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-4 text-sm font-medium text-foreground">{quote?.quote_number}</td>
                        <td className="px-4 py-4 text-sm text-foreground">{quote?.customer_name}</td>
                        <td className="px-4 py-4 text-sm text-foreground capitalize">{quote?.project_type}</td>
                        <td className="px-4 py-4 text-sm text-foreground">€{parseFloat(quote?.quote_amount || 0)?.toLocaleString('nl-NL')}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(quote?.status)}`}>
                            {getStatusLabel(quote?.status)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-muted-foreground">
                          {new Date(quote?.quote_date)?.toLocaleDateString('nl-NL')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/quote-management-system')}>
              <div className="flex items-center space-x-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground">Offertes Beheren</h3>
                  <p className="text-sm text-muted-foreground">Bekijk en beheer alle offertes</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => alert('Klantenbeheer komt binnenkort')}>
              <div className="flex items-center space-x-4">
                <div className="bg-success/10 p-3 rounded-lg">
                  <Icon name="Users" size={24} className="text-success" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground">Klanten</h3>
                  <p className="text-sm text-muted-foreground">Beheer klantgegevens</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => alert('Rapportage komt binnenkort')}>
              <div className="flex items-center space-x-4">
                <div className="bg-info/10 p-3 rounded-lg">
                  <Icon name="BarChart3" size={24} className="text-info" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground">Rapportage</h3>
                  <p className="text-sm text-muted-foreground">Exporteer en analyseer data</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CRMDashboardOverview;